import { PathHelper } from '@sensenet/client-utils'
import { GenericContent } from '@sensenet/default-content-types'
import { CurrentContentContext, useDownload, useLogger, useRepository } from '@sensenet/hooks-react'
import { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { ResponsivePersonalSettings } from '../../context'
import { useGlobalStyles } from '../../globalStyles'
import { useSnRoute } from '../../hooks'
import { getUrlForContent, navigateToAction, supportsRouteActions } from '../../services'
import { useDialog } from '../dialogs'
import { contextMenuODataOptions } from './context-menu-odata-options'

export function useContextMenuActions(content: GenericContent, setActions: (content: GenericContent) => void) {
  const logger = useLogger('context-menu')
  const history = useHistory()
  const repository = useRepository()
  const download = useDownload(content)
  const globalClasses = useGlobalStyles()
  const currentParent = useContext(CurrentContentContext)
  const { openDialog } = useDialog()
  const uiSettings = useContext(ResponsivePersonalSettings)
  const snRoute = useSnRoute()

  const getContentName = () => content.DisplayName ?? content.Name

  const runAction = async (actionName: string, contentType?: string) => {
    switch (actionName) {
      case 'Add': {
        // The caller presents the allowed child types before invoking creation.
        if (!contentType) return
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'new',
            queryParams: { path: content.Path.slice(snRoute.path!.length), 'content-type': contentType },
          })
        } else {
          const url = new URL(
            getUrlForContent({ content, uiSettings, location: history.location, action: 'new' }),
            window.location.origin,
          )
          url.searchParams.set('path', url.searchParams.get('content') || '')
          url.searchParams.delete('content')
          url.searchParams.set('content-type', contentType)
          history.push(`${url.pathname}${url.search}`)
        }
        break
      }
      case 'Upload':
        openDialog({
          name: 'upload',
          props: { uploadPath: content.Path },
          dialogProps: { open: true, fullScreen: false },
        })
        break
      case 'Delete':
        openDialog({ name: 'delete', props: { content: [content] } })
        break
      case 'Edit':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'edit',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'edit' }))
        }
        break
      case 'EditBinary':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'edit-binary',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'edit-binary' }))
        }
        break
      case 'Browse':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'browse',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'browse' }))
        }
        break
      case 'MoveTo':
      case 'CopyTo': {
        const operation = actionName === 'CopyTo' ? 'copy' : 'move'
        openDialog({
          name: 'copy-move',
          props: {
            content: [content],
            currentParent: currentParent!,
            operation,
          },
          dialogProps: { classes: { paper: globalClasses.pickerDialog } },
        })
        break
      }
      case 'Preview':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'preview',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'preview' }))
        }
        break
      case 'CheckOut': {
        try {
          const checkOutresult = await repository.versioning.checkOut(content.Id, contextMenuODataOptions)
          logger.information({
            message: `${getContentName()} checked out successfully.`,
          })
          setActions(checkOutresult.d)
        } catch (error) {
          logger.warning({
            message: `Couldn't check out ${getContentName()}`,
            data: { error },
          })
        }
        break
      }
      case 'CheckIn':
        openDialog({
          name: 'check-in',
          props: {
            content,
            oDataOptions: contextMenuODataOptions,
            onActionSuccess: (checkInResult) => setActions(checkInResult),
          },
        })
        break
      case 'Download':
        download.download()
        break
      case 'WopiOpenView':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'wopi-view',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'wopi-view' }))
        }

        break
      case 'WopiOpenEdit':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'wopi-edit',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'wopi-edit' }))
        }
        break
      case 'Versions':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'version',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'version' }))
        }

        break
      case 'Publish':
        try {
          const publishResult = await repository.versioning.publish(content.Id, contextMenuODataOptions)
          logger.information({ message: `${getContentName()} published successfully.` })
          setActions(publishResult.d)
        } catch (error) {
          logger.warning({ message: `Couldn't publish ${getContentName()}`, data: { error } })
        }
        break
      case 'UndoCheckOut':
        try {
          const undoCheckOutResult = await repository.versioning.undoCheckOut(content.Id, contextMenuODataOptions)
          logger.information({ message: `${getContentName()} reverted successfully.` })
          setActions(undoCheckOutResult.d)
        } catch (error) {
          logger.warning({ message: `Couldn't undo checkout for ${getContentName()}`, data: { error } })
        }
        break
      case 'ForceUndoCheckOut':
        try {
          const forceUndoCheckOutResult = await repository.versioning.forceUndoCheckOut(
            content.Id,
            contextMenuODataOptions,
          )
          logger.information({ message: `${getContentName()} force reverted successfully.` })
          setActions(forceUndoCheckOutResult.d)
        } catch (error) {
          logger.warning({ message: `Couldn't force undo checkout for ${getContentName()}`, data: { error } })
        }
        break
      case 'Approve':
        openDialog({
          name: 'approve',
          props: {
            content,
            oDataOptions: contextMenuODataOptions,
            onActionSuccess: (approveResult) => setActions(approveResult),
          },
        })
        break
      case 'ChangePassword':
        openDialog({
          name: 'change-password',
          props: {
            content,
          },
        })
        break
      case 'SetPermissions':
        if (supportsRouteActions(snRoute) && PathHelper.isInSubTree(content.Path, snRoute.path)) {
          navigateToAction({
            history,
            routeMatch: snRoute.match!,
            action: 'setpermissions',
            queryParams: { content: content.Path.replace(snRoute.path, '') },
          })
        } else {
          history.push(getUrlForContent({ content, uiSettings, location: history.location, action: 'setpermissions' }))
        }
        break
      case 'Restore':
        openDialog({
          name: 'restore',
          props: {
            content,
          },
        })
        break
      case 'ODataActions':
        openDialog({
          name: 'odata-actions',
          props: {
            content,
          },
          dialogProps: { classes: { paper: globalClasses.pickerDialog } },
        })
        break
      /*Az az új hozzáállás, hogy ha nem találja meg az action-t akkor jöjjön be az operation kezelő.
        Ezt majd le kell!! cserélni úgy, hogy vizsgálva legyen a Type-ja*/
      default:
        openDialog({
          name: 'operation',
          props: {
            content,
            OperationName: actionName,
          },
          dialogProps: { classes: { paper: globalClasses.pickerDialog } },
        })
        break
      // logger.warning({ message: `${actionName} is not implemented yet. Try to use it from command palette.` })
    }
  }

  return { runAction }
}
